/**
 * The one phone number normalization and length rule.
 *
 * A phone number is two facts — a country code and a number — and Privacy Bee
 * carries them in two fields everywhere it stores one. This module owns the
 * number half only: it never guesses a country code, and it never attaches one.
 * A caller comparing numbers across countries builds its key from the country
 * code it already holds plus `normalizePhone(number)`.
 */
/**
 * An API-redacted value ("***-**-4567"). It is not a number, so it is never
 * rewritten (normalizing it would invent digits it does not carry) and never
 * valid (nothing can be dialled from it).
 */
const MASKED = /\*{2,}/;
/**
 * A decimal digit in any script. A customer typing on an Arabic or Devanagari
 * keyboard enters ٠٩١٧ or ०९१७, which is the number 0917 — an ASCII-only digit
 * rule reads that as no digits at all and the field rejects a real number.
 *
 * One source, three uses: the class that strips everything else, the scanner
 * that walks the digits that are left, and the single-character test — which
 * must not carry the `g` flag, because a shared regex with `lastIndex` state
 * gives alternating answers to `.test()`.
 */
const DIGIT_CLASS = String.raw `\p{Nd}`;
const NON_DIGITS = new RegExp(`[^${DIGIT_CLASS}]`, "gu");
const DIGITS = new RegExp(DIGIT_CLASS, "gu");
const ONE_DIGIT = new RegExp(`^${DIGIT_CLASS}$`, "u");
/**
 * The national trunk prefix — a dialling instruction, not part of the
 * subscriber number (PH 0917…, UK 020…, DE 030…). Dropping it is what makes
 * "0917 123 4567" and "917 123 4567" one number.
 */
const TRUNK_PREFIX = /^0/;
/**
 * The international access prefix as digits: the `00` dialled for it across the
 * ITU plans. It says exactly what a leading `+` says — a country code follows —
 * so both must produce the same digits, or one number is two comparison keys.
 *
 * `011`, the NANP's access prefix, is deliberately absent: `011` is also how
 * India dials Delhi (trunk `0` plus area code `11`), and stripping it would
 * turn a real Indian number into a shorter, different one.
 */
const ACCESS_PREFIX = "00";
/**
 * The shortest string that can still be a subscriber number: a NANP number is
 * a 3-digit exchange plus a 4-digit line. Below this the field holds a
 * half-typed number, which is the state the modals guard against.
 */
export const PHONE_MIN_DIGITS = 7;
/** E.164 caps a full international number, country code included, at 15 digits. */
export const PHONE_MAX_DIGITS = 15;
/**
 * The digit's value is its distance from the start of its own script's run:
 * every decimal-digit run is ten codepoints in value order (U+0030…U+0039,
 * U+0660…U+0669, U+0966…U+096F), so counting back to the first non-digit counts
 * the value out. Nine is the hard stop — a run is exactly ten long, so a walk
 * that goes further is reading a neighbouring run.
 */
function digitValue(digit) {
    const codePoint = digit.codePointAt(0) ?? 0;
    let value = 0;
    while (value < 9 && ONE_DIGIT.test(String.fromCodePoint(codePoint - value - 1)))
        value++;
    return value;
}
/** Every digit in the value, as ASCII, in the order it was written. */
function toAsciiDigits(value) {
    return value.replace(NON_DIGITS, "").replace(DIGITS, (digit) => String(digitValue(digit)));
}
/**
 * The digits a number is made of, ready to compare or send.
 *
 * A leading `+`, or the `00` access prefix dialled for it, means the input
 * already carries a country code, so nothing may be stripped from the front of
 * what follows. Without one the value is a national-format number and the trunk
 * prefix comes off.
 *
 * A masked value is returned untouched; nullish and empty input give `""`.
 */
export function normalizePhone(value) {
    if (!value)
        return "";
    if (MASKED.test(value))
        return value;
    const digits = toAsciiDigits(value);
    if (value.trimStart().startsWith("+"))
        return digits;
    if (digits.startsWith(ACCESS_PREFIX))
        return digits.slice(ACCESS_PREFIX.length);
    return digits.replace(TRUNK_PREFIX, "");
}
/** The NANP country code, the one plan this module knows how to space out. */
const NANP = "1";
/** A NANP subscriber number: a 3-digit area code, a 3-digit exchange, a 4-digit line. */
const NANP_DIGITS = 10;
/**
 * A number as a reader should see it.
 *
 * Two shapes, and only two: a NANP number reads in its national form,
 * `(555) 123-4567`, because that is how it is written everywhere it is written
 * in the US and Canada; every other number reads as `+CC` and its digits.
 *
 * NO OTHER COUNTRY'S NUMBER IS GROUPED. Grouping is a numbering plan's own
 * rule, not a rhythm that can be guessed from a length — the copies this
 * replaces cut a UK number after two digits and a German one after two, which
 * puts the break in the wrong place for most of both plans, and cut every
 * unlisted country's number in half at the middle. A digit run a reader can
 * copy whole is worth more than a break in a place the plan does not have.
 *
 * `countryCode` is the dial code the record carries beside the number, with or
 * without its `+`. Without one, a number is read as NANP only when its digits
 * say so on their own, exactly as written — ten digits, or eleven beginning `1`
 * (a leading trunk `0` is a foreign plan's, and disqualifies it). Anything else is
 * returned as it arrived, because a value this cannot place is a value it
 * cannot improve, and reprinting it under a country it guessed is how a
 * Cambodian number came to be displayed as American.
 *
 * A masked value ("***-**-4567") is returned untouched — its digits are the
 * API's redaction. Nullish and empty input give `""`.
 */
export function formatPhone(value, countryCode) {
    if (!value)
        return "";
    if (MASKED.test(value))
        return value;
    // A record stores a dial code as either text ("+44") or a number (44); both name the
    // same plan, so both read the same here.
    const dialCode = countryCode ? toAsciiDigits(String(countryCode)) : "";
    if (!dialCode) {
        // The digits exactly as written, trunk prefix included: a trunk zero is a dialling
        // instruction inside some other country's plan, so a value carrying one is not a NANP
        // number. Stripping it first is what made "07911123456" print as "(791) 112-3456".
        const written = toAsciiDigits(value);
        if (written.length === NANP_DIGITS)
            return formatNanp(written);
        if (written.length === NANP_DIGITS + 1 && written.startsWith(NANP)) {
            return formatNanp(written.slice(NANP.length));
        }
        return value;
    }
    const digits = normalizePhone(value);
    if (!digits)
        return value;
    // The number already carrying the dial code the record holds beside it would otherwise
    // print it twice ("+44 442079460018"). A value written in international form says a
    // country code leads it; the NANP also writes its own as a bare eleventh digit.
    const international = value.trimStart().startsWith("+") || toAsciiDigits(value).startsWith(ACCESS_PREFIX);
    const duplicated = digits.startsWith(dialCode) &&
        digits.length > dialCode.length &&
        (international || (dialCode === NANP && digits.length === NANP_DIGITS + 1));
    const national = duplicated ? digits.slice(dialCode.length) : digits;
    return dialCode === NANP && national.length === NANP_DIGITS
        ? formatNanp(national)
        : `+${dialCode} ${national}`;
}
function formatNanp(digits) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}
/**
 * Whether a value holds enough digits to be a dialable number.
 *
 * Both hive phone modals gate their submit on the same rule — at least seven
 * digits — and that rule is the one kept here: it catches the half-typed number
 * without pretending to know any country's numbering plan. The E.164 ceiling is
 * added on top because nothing longer than 15 digits is a phone number in any
 * plan, and an unbounded field is how a pasted paragraph reaches the API.
 *
 * A masked value is never valid: its digits are the API's redaction, not the
 * customer's number.
 */
export function isValidPhone(value) {
    if (!value || MASKED.test(value))
        return false;
    const digits = normalizePhone(value);
    // Nothing dialable starts with a zero once the prefixes are off: no country
    // code begins with one and no numbering plan opens a subscriber number with
    // one. A zero surviving here is a prefix that is not a prefix — "+0…" names
    // no country — so the digits as they stand reach nobody.
    if (digits.startsWith("0"))
        return false;
    return digits.length >= PHONE_MIN_DIGITS && digits.length <= PHONE_MAX_DIGITS;
}
